import { useState } from 'react';
import { Edit2, Save, X, ExternalLink, CheckCircle2, Layers } from 'lucide-react';

const INITIAL_ENVIRONMENTS = [
  {
    id: 'dev',
    name: 'Development',
    slug: 'dev',
    gcpProjectId: 'my-project-dev',
    region: 'us-central1',
    color: '#3b82f6',
    status: 'active',
    artifactRegistry: 'us-central1-docker.pkg.dev/my-project-dev/cloud-images',
    cloudRunRegion: 'us-central1',
    autoDeployBranch: 'develop',
    requireApproval: false,
    services: 5,
    lastActivity: new Date(Date.now() - 3600000).toISOString(),
  },
  {
    id: 'staging',
    name: 'Staging',
    slug: 'staging',
    gcpProjectId: 'my-project-staging',
    region: 'us-central1',
    color: '#f59e0b',
    status: 'active',
    artifactRegistry: 'us-central1-docker.pkg.dev/my-project-staging/cloud-images',
    cloudRunRegion: 'us-central1',
    autoDeployBranch: 'main',
    requireApproval: false,
    services: 5,
    lastActivity: new Date(Date.now() - 21600000).toISOString(),
  },
  {
    id: 'production',
    name: 'Production',
    slug: 'production',
    gcpProjectId: 'my-project-prod',
    region: 'us-central1',
    color: '#22c55e',
    status: 'active',
    artifactRegistry: 'us-central1-docker.pkg.dev/my-project-prod/cloud-images',
    cloudRunRegion: 'us-central1',
    autoDeployBranch: null,
    requireApproval: true,
    services: 5,
    lastActivity: new Date(Date.now() - 86400000).toISOString(),
  },
];

const REGIONS = ['us-central1', 'us-east1', 'us-west1', 'europe-west1', 'europe-west3', 'asia-east1', 'asia-northeast1'];

function EnvCard({ env, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(env);

  const handleSave = () => {
    onSave(form);
    setEditing(false);
  };

  const field = (label, key, type = 'text', options = null) => (
    <div key={key}>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {options ? (
        <select className="select text-xs py-1.5" value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'checkbox' ? (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="accent-accent-purple"
            checked={!!form[key]}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
          />
          <span className="text-xs text-slate-700">{label}</span>
        </label>
      ) : (
        <input
          type={type}
          className="input text-xs py-1.5"
          value={form[key] || ''}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        />
      )}
    </div>
  );

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${env.color}20` }}>
            <Layers size={16} style={{ color: env.color }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{env.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CheckCircle2 size={10} className="text-green-400" />
              <span className="text-xs text-slate-500">Active · {env.services} services</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button onClick={handleSave} className="btn-primary py-1 px-3 text-xs"><Save size={12} /> Save</button>
              <button onClick={() => { setForm(env); setEditing(false); }} className="btn-ghost py-1 px-2 text-xs"><X size={12} /></button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="btn-ghost py-1 px-3 text-xs"><Edit2 size={12} /> Edit</button>
          )}
        </div>
      </div>

      {/* Body */}
      {editing ? (
        <div className="p-5 grid grid-cols-2 gap-4">
          {field('GCP Project ID', 'gcpProjectId')}
          {field('Region', 'cloudRunRegion', 'text', REGIONS)}
          {field('Artifact Registry', 'artifactRegistry')}
          {field('Auto-deploy branch', 'autoDeployBranch')}
          <div className="col-span-2">
            {field('Require approval for deployments', 'requireApproval', 'checkbox')}
          </div>
        </div>
      ) : (
        <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-3">
          {[
            { label: 'GCP Project', value: env.gcpProjectId },
            { label: 'Region', value: env.cloudRunRegion },
            { label: 'Artifact Registry', value: env.artifactRegistry, mono: true, small: true },
            { label: 'Auto-deploy branch', value: env.autoDeployBranch || 'manual only' },
            { label: 'Approval required', value: env.requireApproval ? 'Yes' : 'No' },
            { label: 'Last activity', value: new Date(env.lastActivity).toLocaleString() },
          ].map(row => (
            <div key={row.label}>
              <p className="text-xs text-slate-500 mb-0.5">{row.label}</p>
              <p className={`text-sm text-slate-900 ${row.mono ? 'font-mono text-xs' : ''}`}>{row.value}</p>
            </div>
          ))}

          <div className="col-span-2 pt-2 border-t border-slate-200">
            <a
              href={`https://console.cloud.google.com/run?project=${env.gcpProjectId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-accent-purple hover:text-accent-purple-light transition-colors"
            >
              <ExternalLink size={11} /> Open in GCP Console
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Environments() {
  const [envs, setEnvs] = useState(INITIAL_ENVIRONMENTS);

  const handleSave = (updated) => {
    setEnvs(es => es.map(e => e.id === updated.id ? updated : e));
  };

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Environments</h2>
        <p className="text-sm text-slate-500">Manage GCP projects linked to each environment</p>
      </div>

      <div className="space-y-4">
        {envs.map(env => (
          <EnvCard key={env.id} env={env} onSave={handleSave} />
        ))}
      </div>
    </div>
  );
}
